import { useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { ProgressBar } from '../../ui/ProgressBar';

/** Fraction of the timeline that must be watched before the slide unlocks. */
const WATCH_THRESHOLD = 0.95;

/**
 * Inline HTML5 video with watch-gating. Coverage is tracked per whole second
 * actually played (a Set, so re-watching a clip can't inflate it and seeking
 * ahead never credits skipped content); once WATCH_THRESHOLD of the duration
 * is covered the block reports itself watched and "Next" unlocks.
 *
 * Native controls keep it keyboard-operable; it never autoplays, so reduced
 * motion is honoured by construction. When `gated` is false (builder preview)
 * it's a plain player with no tracking.
 */
export function VideoViewer({
  url,
  gated = false,
  watched = false,
  onWatched,
}: {
  url: string;
  gated?: boolean;
  watched?: boolean;
  onWatched?: () => void;
}) {
  const [pct, setPct] = useState(watched ? 1 : 0);
  const seconds = useRef<Set<number>>(new Set());
  const lastTime = useRef(0);
  const seeking = useRef(false);
  const done = useRef(watched);

  function onTimeUpdate(e: React.SyntheticEvent<HTMLVideoElement>) {
    if (!gated || done.current) return;
    const video = e.currentTarget;
    const t = video.currentTime;
    const dt = t - lastTime.current;
    lastTime.current = t;
    // Count this second only on continuous forward playback: a seek produces a
    // large/negative jump and is skipped, so jumping to the end never counts.
    if (seeking.current || dt < 0 || dt >= 1.5) return;
    seconds.current.add(Math.floor(t));
    const total = Math.max(1, Math.floor(video.duration || 0));
    const frac = Math.min(1, seconds.current.size / total);
    if (frac >= WATCH_THRESHOLD) {
      done.current = true;
      setPct(1);
      onWatched?.();
    } else {
      setPct(frac);
    }
  }

  return (
    <div className="space-y-2">
      <video
        src={url}
        controls
        playsInline
        preload="metadata"
        onSeeking={() => {
          seeking.current = true;
        }}
        onSeeked={(e) => {
          seeking.current = false;
          lastTime.current = e.currentTarget.currentTime;
        }}
        onTimeUpdate={onTimeUpdate}
        className="w-full rounded-token-sm border border-line/10 bg-black"
      />
      {gated ? (
        done.current ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-success-soft-fg">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            Watched
          </p>
        ) : (
          <div aria-live="polite">
            <ProgressBar value={Math.round(pct * 100)} total={100} ariaLabel="Video watched" />
            <p className="mt-1 text-xs text-fg-muted">
              Watched {Math.round(pct * 100)}% — keep watching to continue.
            </p>
          </div>
        )
      ) : null}
    </div>
  );
}
